import React, { useMemo, useState } from "react";
import type { MenuItemResponse } from "@/api/lib/db/types";
import Icon from "../../../lib/components/custom/Icon";
import { Button } from "../../../lib/components/ui/button";
import SearchInput from "../../../lib/components/ui/search-input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../../../lib/components/ui/select";
import { useApi } from "../../../lib/hooks/api/use-api";
import { AdminItemCard } from "./AdminItemCard";
import MenuItemDialog from "./MenuItemDialog";
import { MenuItemsSkeleton } from "./MenuItemsSkeleton";

type MenuItem = MenuItemResponse;

export function MenuItemsTab() {
	const [submittedSearchQuery, setSubmittedSearchQuery] = useState("");
	const [isSearchMode, setIsSearchMode] = useState(false);
	const [categoryFilter, setCategoryFilter] = useState<string>("all");
	const [availabilityFilter, setAvailabilityFilter] = useState<string>("all");

	const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
	const [editingItem, setEditingItem] = useState<MenuItem | null>(null);
	const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

	const { menu } = useApi();

	const {
		data: menuItemsData,
		isLoading,
		refetch,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
	} = menu.getItemsInfinite({
		category:
			isSearchMode && submittedSearchQuery
				? undefined
				: categoryFilter !== "all"
					? categoryFilter
					: undefined,
		limit: 10,
	});

	const {
		data: categories,
		isLoading: categoriesLoading,
		error: categoriesError,
	} = menu.getCategories();
	const { data: searchData, isLoading: searchLoading } = menu.searchItems({
		q: submittedSearchQuery,
		limit: 50,
	});

	const loadMore = () => {
		if (hasNextPage && !isFetchingNextPage) {
			fetchNextPage();
		}
	};

	const categoryDisplayNames = React.useMemo(() => {
		if (!categories) return {};
		const displayMap: Record<string, string> = {};
		categories.forEach((category) => {
			const categoryName =
				typeof category === "string"
					? category
					: category.name || category.slug;
			displayMap[categoryName] =
				categoryName.charAt(0).toUpperCase() + categoryName.slice(1);
		});
		return displayMap;
	}, [categories]);

	const allItems = useMemo(() => {
		if (isSearchMode && searchData) return searchData.items || [];
		if (!menuItemsData?.pages) return [];
		return menuItemsData.pages.flatMap((page) => page.items || []);
	}, [menuItemsData?.pages, searchData, isSearchMode]);

	const displayItems = useMemo(() => {
		if (isSearchMode && submittedSearchQuery) return allItems;
		return allItems.filter((item) => {
			const matchesCategory =
				categoryFilter === "all" ||
				item.category.toLowerCase() === categoryFilter.toLowerCase();
			const matchesAvailability =
				availabilityFilter === "all" ||
				(availabilityFilter === "available" && item.isAvailable) ||
				(availabilityFilter === "unavailable" && !item.isAvailable);
			return matchesCategory && matchesAvailability;
		});
	}, [
		allItems,
		submittedSearchQuery,
		isSearchMode,
		categoryFilter,
		availabilityFilter,
	]);

	const handleEditItem = (itemId: number, item: any) => {
		setEditingItem(item);
		setIsEditDialogOpen(true);
	};

	if (
		isLoading ||
		(isSearchMode && submittedSearchQuery && searchLoading) ||
		categoriesLoading
	) {
		return <MenuItemsSkeleton />;
	}

	if (categoriesError) {
		return (
			<div className="flex items-center justify-center py-20">
				<div className="text-center">
					<Icon name="X" className="w-12 h-12 text-destructive mx-auto mb-4" />
					<h3 className="text-lg font-semibold mb-2">
						Failed to load categories
					</h3>
					<p className="text-muted-foreground">
						Unable to load menu categories.
					</p>
				</div>
			</div>
		);
	}

	return (
		<>
			<div className="flex flex-col-reverse lg:flex-row gap-4 lg:items-center justify-between">
				<div className="flex flex-col sm:flex-row gap-4 flex-1">
					<div className="w-full sm:max-w-xs">
						<SearchInput
							placeholder="Search menu items..."
							onSubmit={(query) => {
								setSubmittedSearchQuery(query);
								setIsSearchMode(true);
							}}
						/>
					</div>

					<div className="flex gap-2">
						{isSearchMode && (
							<Button
								variant="outline"
								onClick={() => {
									setIsSearchMode(false);
									setSubmittedSearchQuery("");
								}}
							>
								<Icon name="X" className="w-4 h-4" />
								Clear
							</Button>
						)}

						<Select value={categoryFilter} onValueChange={setCategoryFilter}>
							<SelectTrigger className="w-[140px] bg-card">
								<SelectValue placeholder="Category" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">All Categories</SelectItem>
								{(categories || []).map((category) => {
									const categoryName =
										typeof category === "string"
											? category
											: category.name || category.slug;
									return (
										<SelectItem key={categoryName} value={categoryName}>
											{categoryDisplayNames[categoryName] || categoryName}
										</SelectItem>
									);
								})}
							</SelectContent>
						</Select>

						<Select
							value={availabilityFilter}
							onValueChange={setAvailabilityFilter}
						>
							<SelectTrigger className="w-[130px] bg-card">
								<SelectValue placeholder="Status" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">All Status</SelectItem>
								<SelectItem value="available">Available</SelectItem>
								<SelectItem value="unavailable">Unavailable</SelectItem>
							</SelectContent>
						</Select>
					</div>
				</div>

				<Button variant="primary" onClick={() => setIsCreateDialogOpen(true)}>
					<Icon name="Plus" className="w-4 h-4" />
					Add Item
				</Button>
			</div>

			<div className="grid mt-24 grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-10 gap-y-24 min-h-[400px]">
				{displayItems.length === 0 ? (
					<div className="col-span-full flex flex-col items-center justify-center py-20 bg-card rounded-xl border border-dashed border-border/60">
						<div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
							<Icon
								name="Utensils"
								className="w-8 h-8 text-muted-foreground/50"
							/>
						</div>
						<h3 className="text-lg font-medium text-foreground mb-1">
							{isSearchMode && submittedSearchQuery
								? "No matching items"
								: "No menu items yet"}
						</h3>
					</div>
				) : (
					displayItems.map((item) => (
						<div key={item.id}>
							<AdminItemCard item={item as any} onEdit={handleEditItem} />
						</div>
					))
				)}
			</div>

			{hasNextPage && !isSearchMode && (
				<div className="flex justify-center mt-8 pb-8">
					<Button
						onClick={loadMore}
						variant="outline"
						disabled={isFetchingNextPage}
						className="bg-card hover:bg-muted min-w-[150px]"
					>
						{isFetchingNextPage ? (
							<span className="flex items-center gap-2">
								<Icon name="Loader" className="w-3 h-3 animate-spin" />{" "}
							</span>
						) : (
							"Load More Items"
						)}
					</Button>
				</div>
			)}

			<MenuItemDialog
				mode="create"
				open={isCreateDialogOpen}
				onOpenChange={setIsCreateDialogOpen}
				categories={categories || []}
				onSuccess={() => refetch()}
			/>

			<MenuItemDialog
				mode="edit"
				open={isEditDialogOpen}
				onOpenChange={setIsEditDialogOpen}
				item={editingItem as any}
				categories={categories || []}
				onSuccess={() => refetch()}
			/>
		</>
	);
}
